import { useEffect, useMemo, useState } from 'react';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsCard,
  PortalDsField,
  PortalDsInput,
  PortalDsSectionHeader,
  PortalDsSelect,
  portalDs,
} from 'src/components/portal-design-system';
import {
  useGetEtlVerificationPolicyQuery,
  useUpdateEtlVerificationPolicyMutation,
} from 'src/redux/operations/operations.endpoints';
import type {
  MartTargetPolicy,
  VerificationCheckSeverity,
  VerificationPolicy,
} from '../types/operations.types';

const RECON_METRICS = [
  { key: 'spend' as const, label: 'Spend' },
  { key: 'impressions' as const, label: 'Impressions' },
  { key: 'clicks' as const, label: 'Clicks' },
];

const MART_SEVERITY_OPTIONS: { value: VerificationCheckSeverity; label: string }[] = [
  { value: 'fail', label: 'Needs action (fail)' },
  { value: 'warn', label: 'Review (warn)' },
  { value: 'ignore', label: 'Ignore' },
];

const MART_CHECKS: { key: keyof VerificationPolicy['mart']['severity']; label: string; hint: string }[] = [
  {
    key: 'row_count',
    label: 'Row count',
    hint: 'Zero rows for a source/table on the active date.',
  },
  {
    key: 'null_rate',
    label: 'Null rates',
    hint: 'High null rates on impressions or spend.',
  },
  {
    key: 'spend_total',
    label: 'Spend drift (staging → mart)',
    hint: 'Upstream vs mart spend drift within the pipeline.',
  },
  {
    key: 'raw_to_mart_parity',
    label: 'Row-count parity',
    hint: 'Staging/raw vs mart row-count gap (often noisy).',
  },
];

function clonePolicy(policy: VerificationPolicy): VerificationPolicy {
  return structuredClone(policy);
}

function getErrorMessage(error: unknown): string {
  return (error as { message?: string; data?: { status?: { status_message?: string } } })?.data
    ?.status?.status_message
    ?? (error as { message?: string })?.message
    ?? 'Could not save verification policy.';
}

export function VerificationPolicyCard() {
  const policyQuery = useGetEtlVerificationPolicyQuery();
  const [updatePolicy, updateState] = useUpdateEtlVerificationPolicyMutation();
  const [draft, setDraft] = useState<VerificationPolicy | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  useEffect(() => {
    if (policyQuery.data?.policy) {
      const policy = clonePolicy(policyQuery.data.policy);
      policy.mart.targets = policy.mart.targets ?? {};
      setDraft(policy);
    }
  }, [policyQuery.data]);

  const isDirty = useMemo(() => {
    if (!draft || !policyQuery.data) return false;
    return JSON.stringify(draft) !== JSON.stringify(policyQuery.data.policy);
  }, [draft, policyQuery.data]);

  const handleSave = async () => {
    if (!draft) return;
    setLocalError(null);
    setSavedNotice(null);
    try {
      await updatePolicy({ policy: draft }).unwrap();
      setSavedNotice('Policy saved. It applies to future verification runs only.');
    } catch (error) {
      setLocalError(getErrorMessage(error));
    }
  };

  const handleReset = async () => {
    setLocalError(null);
    setSavedNotice(null);
    try {
      await updatePolicy({ reset: true }).unwrap();
      setSavedNotice('Policy reset to YAML/code defaults.');
    } catch (error) {
      setLocalError(getErrorMessage(error));
    }
  };

  const setReconPct = (
    metric: keyof VerificationPolicy['reconciliation'],
    field: 'warning_pct' | 'fail_pct',
    value: string,
  ) => {
    const parsed = Number(value);
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        reconciliation: {
          ...current.reconciliation,
          [metric]: {
            ...current.reconciliation[metric],
            [field]: Number.isFinite(parsed) ? parsed : 0,
          },
        },
      };
    });
  };

  const setMartSeverity = (
    check: keyof VerificationPolicy['mart']['severity'],
    value: VerificationCheckSeverity,
  ) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        mart: {
          ...current.mart,
          severity: {
            ...current.mart.severity,
            [check]: value,
          },
        },
      };
    });
  };

  const targetEntries = Object.entries(draft?.mart.targets ?? {}).sort(([left], [right]) => (
    left.localeCompare(right)
  ));

  const setTargetExpectedZero = (targetKey: string, expectedZero: boolean) => {
    setDraft((current) => {
      if (!current) return current;
      const existing = current.mart.targets[targetKey] ?? { expected_zero: false };
      return {
        ...current,
        mart: {
          ...current.mart,
          targets: {
            ...current.mart.targets,
            [targetKey]: {
              ...existing,
              expected_zero: expectedZero,
            },
          },
        },
      };
    });
  };

  const setTargetRowCountSeverity = (
    targetKey: string,
    value: VerificationCheckSeverity | '',
  ) => {
    setDraft((current) => {
      if (!current) return current;
      const existing = current.mart.targets[targetKey] ?? { expected_zero: false };
      const nextSeverity = { ...(existing.severity ?? {}) };
      if (!value) {
        delete nextSeverity.row_count;
      } else {
        nextSeverity.row_count = value;
      }
      const nextTarget: MartTargetPolicy = {
        ...existing,
        ...(Object.keys(nextSeverity).length ? { severity: nextSeverity } : {}),
      };
      if (!nextTarget.severity) {
        delete nextTarget.severity;
      }
      return {
        ...current,
        mart: {
          ...current.mart,
          targets: {
            ...current.mart.targets,
            [targetKey]: nextTarget,
          },
        },
      };
    });
  };

  const addTargetOverride = () => {
    const targetKey = window.prompt(
      'Target key (datasource.connector), e.g. linkedin_ads.performance',
    )?.trim();
    if (!targetKey || !targetKey.includes('.')) return;
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        mart: {
          ...current.mart,
          targets: {
            ...current.mart.targets,
            [targetKey]: current.mart.targets[targetKey] ?? { expected_zero: false },
          },
        },
      };
    });
  };

  const removeTargetOverride = (targetKey: string) => {
    setDraft((current) => {
      if (!current) return current;
      const nextTargets = { ...current.mart.targets };
      delete nextTargets[targetKey];
      return {
        ...current,
        mart: {
          ...current.mart,
          targets: nextTargets,
        },
      };
    });
  };

  return (
    <PortalDsCard aria-labelledby="verification-policy-heading">
      <PortalDsSectionHeader
        overline="Policy"
        title="Verification severity"
        description="Control which checks need action vs review. Changes apply to the next daily verification run — historic results are unchanged."
        titleId="verification-policy-heading"
        actions={(
          <>
            <PortalDsButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void handleReset()}
              disabled={updateState.isLoading || policyQuery.isLoading}
            >
              Reset to defaults
            </PortalDsButton>
            <PortalDsButton
              type="button"
              variant="accent"
              size="sm"
              onClick={() => void handleSave()}
              disabled={!isDirty || updateState.isLoading || !draft}
            >
              {updateState.isLoading ? 'Saving…' : 'Save policy'}
            </PortalDsButton>
          </>
        )}
      />

      <div className="space-y-6">
        {policyQuery.isLoading || !draft ? (
          <p className={portalDs.bodySm}>Loading verification policy…</p>
        ) : (
          <>
            <p className={portalDs.caption}>
              {policyQuery.data?.has_override
                ? `Custom override${policyQuery.data.updated_by ? ` by ${policyQuery.data.updated_by}` : ''}${
                  policyQuery.data.updated_at ? ` · ${new Date(policyQuery.data.updated_at).toLocaleString()}` : ''
                }`
                : 'Using YAML/code defaults (no DB override).'}
            </p>

            <section className="space-y-3">
              <h3 className={portalDs.headingH3}>Vendor reconciliation (external)</h3>
              <p className={portalDs.bodySm}>
                Drift at or below Review % is a pass. Between Review and Needs action is a warning.
                Above Needs action fails the run.
              </p>
              <div className="grid gap-4 md:grid-cols-3">
                {RECON_METRICS.map((metric) => (
                  <div key={metric.key} className="space-y-3 rounded-lg border border-[#EDEEF2] p-4">
                    <div className={portalDs.filterLabel}>{metric.label}</div>
                    <PortalDsField label="Review threshold (%)" htmlFor={`recon-${metric.key}-warn`}>
                      <PortalDsInput
                        id={`recon-${metric.key}-warn`}
                        type="number"
                        min={0}
                        step="0.1"
                        value={draft.reconciliation[metric.key].warning_pct}
                        onChange={(event) => setReconPct(metric.key, 'warning_pct', event.target.value)}
                      />
                    </PortalDsField>
                    <PortalDsField label="Needs action (%)" htmlFor={`recon-${metric.key}-fail`}>
                      <PortalDsInput
                        id={`recon-${metric.key}-fail`}
                        type="number"
                        min={0}
                        step="0.1"
                        value={draft.reconciliation[metric.key].fail_pct}
                        onChange={(event) => setReconPct(metric.key, 'fail_pct', event.target.value)}
                      />
                    </PortalDsField>
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className={portalDs.headingH3}>Mart checks</h3>
              <p className={portalDs.bodySm}>
                Choose whether each mart check class should fail the job, warn for review, or be ignored.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {MART_CHECKS.map((check) => (
                  <PortalDsField
                    key={check.key}
                    label={check.label}
                    htmlFor={`mart-severity-${check.key}`}
                    hint={check.hint}
                  >
                    <PortalDsSelect
                      id={`mart-severity-${check.key}`}
                      value={draft.mart.severity[check.key]}
                      onChange={(event) => setMartSeverity(
                        check.key,
                        event.target.value as VerificationCheckSeverity,
                      )}
                    >
                      {MART_SEVERITY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </PortalDsSelect>
                  </PortalDsField>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className={portalDs.headingH3}>Per-target mart overrides</h3>
              <p className={portalDs.bodySm}>
                Override check severity for a specific datasource.connector target, or mark an
                enabled connector as expected-zero so zero row counts warn instead of fail.
              </p>
              <div className="space-y-3">
                {targetEntries.length === 0 ? (
                  <p className={portalDs.caption}>No per-target overrides configured.</p>
                ) : null}
                {targetEntries.map(([targetKey, targetPolicy]) => (
                  <div
                    key={targetKey}
                    className="space-y-3 rounded-lg border border-[#EDEEF2] p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className={portalDs.filterLabel}>{targetKey}</div>
                      <PortalDsButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTargetOverride(targetKey)}
                      >
                        Remove
                      </PortalDsButton>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={targetPolicy.expected_zero}
                        onChange={(event) => setTargetExpectedZero(
                          targetKey,
                          event.target.checked,
                        )}
                      />
                      Expected zero rows (warn instead of fail on row count)
                    </label>
                    <PortalDsField
                      label="Row count severity override"
                      htmlFor={`target-row-count-${targetKey}`}
                      hint="Leave blank to use the global mart row-count severity."
                    >
                      <PortalDsSelect
                        id={`target-row-count-${targetKey}`}
                        value={targetPolicy.severity?.row_count ?? ''}
                        onChange={(event) => setTargetRowCountSeverity(
                          targetKey,
                          event.target.value as VerificationCheckSeverity | '',
                        )}
                      >
                        <option value="">Use global default</option>
                        {MART_SEVERITY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </PortalDsSelect>
                    </PortalDsField>
                  </div>
                ))}
              </div>
              <PortalDsButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={addTargetOverride}
              >
                Add target override
              </PortalDsButton>
            </section>
          </>
        )}

        {localError ? (
          <PortalDsAlert variant="danger" title="Could not update policy">
            {localError}
          </PortalDsAlert>
        ) : null}

        {savedNotice ? (
          <PortalDsAlert variant="success" title="Saved">
            {savedNotice}
          </PortalDsAlert>
        ) : null}

        {policyQuery.isError ? (
          <PortalDsAlert variant="danger" title="Could not load policy">
            {getErrorMessage(policyQuery.error)}
          </PortalDsAlert>
        ) : null}
      </div>
    </PortalDsCard>
  );
}
